import {ChangeEvent, useCallback, useEffect, useState} from 'react';
import {Box, Button, FormControlLabel, Typography} from '@mui/material';
import AurCheckbox from '../aur-checkbox/AurCheckbox';
import {AurTextField} from '../aur-text-field/AurTextField';
import {ReactComponent as FailedIcon} from '../../../assests/svg/info-outlined-red.svg';
import {useSelector} from 'react-redux';
import {getUTCDateTimeForDisplay} from '../../../utils/utils';
import {selectUser} from '../../../redux/slices/userSlice';
import {t} from 'i18next';
import '../../../../src/styles/components/common/_aur-acknowledgement.scss';
import {AurLightTooltip} from '../aur-light-tooltip/aur-light-tooltip';

export type AcknowlegmentProps = {
  labelHeading?: string;
  labelSubText?: string;
  showCheckbox?: boolean;
  labelCheckbox?: string;
  showIntiateButton?: boolean;
  initateBtnEnabled?: boolean;
  initialAcknowledgementDateTime?: string;
  onInitiateClick?: () => void;
  onAcknowledgeChange?: (
    isAcknowledged: boolean,
    acknowledgementDateTime?: string
  ) => void;
};

const AurAcknowlegment: React.FC<AcknowlegmentProps> = (props) => {
  const {
    labelHeading,
    labelSubText,
    labelCheckbox,
    showCheckbox = false,
    showIntiateButton = false,
    initateBtnEnabled = false,
    initialAcknowledgementDateTime,
    onInitiateClick,
    onAcknowledgeChange,
  } = props;

  const [confirmed, setConfirmed] = useState(false);
  const [ackText, setAckText] = useState('');
  const [isError, setIsError] = useState(false);
  const [ackTimestamp, setAckTimestamp] = useState<string | null>(null);
  const [ackErrorMsg, setAckErrorMsg] = useState<string | null>(null);
  const user = useSelector(selectUser);

  const ACKNOWLEDGEMENT_PHRASE = t('acknowledgement.i_understand');

  const canSubmit =
    (showCheckbox ? confirmed : true) &&
    ackText.trim() === ACKNOWLEDGEMENT_PHRASE;

  useEffect(() => {
    if (initialAcknowledgementDateTime) {
      setAckTimestamp(initialAcknowledgementDateTime);
      setAckText(ACKNOWLEDGEMENT_PHRASE);
    }
  }, [ACKNOWLEDGEMENT_PHRASE, initialAcknowledgementDateTime]);

  const handleConfirm = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      setConfirmed(checked);
      if (!checked) {
        setAckText('');
        setIsError(false);
        setAckErrorMsg(null);
        setAckTimestamp(null);
        onAcknowledgeChange?.(false);
      }
    },
    [onAcknowledgeChange]
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;
      value = value.trimStart();
      value = value.replace(/\s{2,}/g, ' ');

      setAckText(value);

      const text = value.trim();
      if (text === ACKNOWLEDGEMENT_PHRASE) {
        setIsError(false);
        setAckErrorMsg(null);

        const acknowledgementDateTime = t('acknowledgement.signed_by', {
          user: user?.name,
          date: getUTCDateTimeForDisplay(new Date()),
        });
        setAckTimestamp(acknowledgementDateTime);
        onAcknowledgeChange?.(true, acknowledgementDateTime);
      } else {
        setIsError(true);
        setAckTimestamp(null);
        setAckErrorMsg(t('acknowledgement.error_msg'));
        onAcknowledgeChange?.(false);
      }
    },
    [ACKNOWLEDGEMENT_PHRASE, onAcknowledgeChange, user?.name]
  );

  return (
    <Box className="acknoledgement-container">
      <Box className="acknowledgement-section">
        <Box className="acknowledgment">
          {showCheckbox && (
            <FormControlLabel
              sx={{
                alignItems: 'flex-start',
              }}
              control={
                <AurCheckbox
                  checked={confirmed}
                  onChange={handleConfirm}
                  sx={{marginTop: '-8px'}}
                  inputProps={{'aria-required': true}}
                />
              }
              label={
                <Typography
                  tabIndex={0}
                  className="typo-text subhead3_size checkbox-label"
                  sx={{whiteSpace: 'pre-line'}}
                >
                  {labelCheckbox}
                </Typography>
              }
            />
          )}
          <Box
            className={`acknowledgement-text-field ${
              showCheckbox ? 'with-checkbox' : ''
            }`}
          >
            {!showCheckbox && (
              <Typography
                component="h2"
                tabIndex={0}
                className="typo-text subhead2_size"
              >
                {labelHeading}
              </Typography>
            )}
            <Typography
              variant="body2"
              tabIndex={0}
              component="p"
              className={
                showCheckbox
                  ? `typo-text subhead3_size ack-heading3 ${confirmed ? 'active' : ''}`
                  : 'typo-text body2_size ack-subText'
              }
            >
              {labelSubText}
            </Typography>
            <AurTextField
              error={isError}
              value={ackText}
              disabled={showCheckbox ? !confirmed : false}
              onChange={handleChange}
              helperText={
                isError ? (
                  ackErrorMsg
                ) : (
                  <AurLightTooltip title={t('common.yearMonthDate')}>
                    <span>{ackTimestamp}</span>
                  </AurLightTooltip>
                )
              }
              label={t('acknowledgement.type_i_understand')}
              variant="filled"
              ariaLabel={t('acknowledgement.type_i_understand')}
              className={
                showCheckbox
                  ? `aur-textfield acknowledgment-input`
                  : 'aur-textfield acknowledgment-input-without-checkbox'
              }
              InputProps={{
                endAdornment: isError ? (
                  <FailedIcon height={20} width={20} />
                ) : null,
              }}
            />
            {showIntiateButton && (
              <Button
                variant="contained"
                className="typo-text buttonLarge_size custom-button button-logo"
                sx={{
                  textTransform: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  '&.MuiButton-root:focus': {
                    border: 'none !important',
                  },
                  '&.MuiButton-root:focus-visible': {
                    border: 'none !important',
                  },
                }}
                type="submit"
                disabled={!(canSubmit && initateBtnEnabled)}
                aria-disabled={!(canSubmit && initateBtnEnabled)}
                onClick={onInitiateClick}
              >
                {t('acknowledgement.initiateProcessing')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AurAcknowlegment;
